import { useContext } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import i18next from 'i18next';

import { warehouses } from 'constants/warehouses';

import { TechnicalData } from 'types/api/product.type';

import { ordersApi } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { ordersDataService } from 'services';
import { pdfService } from 'services/pdf.service';

import { SingleProductContext } from 'contexts';

import { useCatalogueLabels } from 'hooks';

import { getSearchParams } from 'helpers/getSearchParams';

import { Button } from 'components/Button';
import { IconName } from 'components/Icon';

export type ButtonType = {
  id: number;
  label: string;
  icon?: IconName;
  onClick: () => void;
  isHidden?: boolean;
};

const useActions = ({ technicalData }: { technicalData?: TechnicalData }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id, genre, category } = useParams();
  const { itemCode, mainFabricCode, itemColorCode } = getSearchParams(searchParams);
  const { language } = i18next;
  const { t } = useTranslation();
  const { categoryLabel } = useCatalogueLabels();
  const {
    isOrderModalVisibleSet,
    galleryUrls,
    product,
    isGalleryVisibleSet,
    productOrder,
    productOrderSet,
    title,
  } = useContext(SingleProductContext);

  const { orderId, season, warehouse } = useAppSelector((state) => state.settings);
  const dispatch = useAppDispatch();

  const isBlackCatalogue = warehouse.code === warehouses[0].code;
  const cannotPurchase = !isBlackCatalogue && !product?.canPurchase;

  const buttonsData: ButtonType[] = [
    {
      id: 1,
      label: t('product.gallery'),
      onClick: () => isGalleryVisibleSet(true),
      isHidden: galleryUrls?.length === 0,
    },
    {
      id: 2,
      label: t('product.downloadPdf'),
      icon: 'FiDownloadCloud',
      onClick: () => {
        const dataInSelectedLang =
          language === 'en'
            ? {
                ...technicalData,
                styleCodeAndStyleDesc: product?.englishItemMainTitle,
                colorDescription: product?.englishColorDescription,
                description: technicalData?.englishDescription,
                accessories: technicalData?.englishAccessories,
              }
            : {
                ...technicalData,
                styleCodeAndStyleDesc: product?.italianItemMainTitle,
                colorDescription: product?.italianColorDescription,
                description: technicalData?.italianDescription,
                accessories: technicalData?.italianAccessories,
              };
        const formattedProduct = {
          title,
          ...product,
          ...dataInSelectedLang,
          nodeName: category ? categoryLabel(category) : category,
          season,
        };

        pdfService
          .getSingleProductPDF(formattedProduct)
          .then((pdfRes) => {
            const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', `COLMAR-${technicalData?.productId}.pdf`);
            document.body.appendChild(link);
            link.click();
          })
          .catch(() => {
            toast.error(t('pdf.downloadFailed'));
          });
      },
    },
    // {
    //   id: 3,
    //   label: t('add to list'),
    //   icon: 'FiHeart',
    //   onClick: () => {
    //     isSelectionModalVisibleSet(true);
    //   },
    // },
    {
      id: 4,
      label: t('product.orderButton'),
      icon: 'FiShoppingCart',
      onClick: () => {
        if (!orderId) {
          isOrderModalVisibleSet(true);
          return;
        }

        ordersDataService
          .addItem(orderId, {
            productCode: itemCode ?? '',
            mainFabricCode: mainFabricCode ?? '',
            colorCode: itemColorCode ?? '',
            sizes: [],
          })
          .then((res) => {
            productOrderSet((state) => ({
              ...state,
              available: true,
              itemId: res.data?.itemId,
              sizes: res.data?.sizes,
            }));
            dispatch(ordersApi.util.invalidateTags([{ type: 'OrderHeader', id: orderId }]));
            toast.success(t('product.addedSuccessfully'));
          })
          .catch(() => {
            toast.error(t('product.errorAdding'));
          });
      },
      isHidden: !product?.active || productOrder?.available || cannotPurchase,
    },
    {
      id: 5,
      icon: 'FiShoppingCart',
      label: t('product.removeFromCurrentOrder'),
      onClick: () => {
        if (orderId && productOrder?.itemId) {
          ordersDataService.deleteItem(orderId, productOrder.itemId).then(() => {
            productOrderSet((state) => ({ ...state, available: false, itemId: undefined }));
            dispatch(ordersApi.util.invalidateTags([{ type: 'OrderHeader', id: orderId }]));
            toast.success(t('product.removedSuccessfully'));
          });
        } else {
          isOrderModalVisibleSet(true);
        }
      },
      isHidden: !productOrder?.available,
    },
    {
      id: 6,
      label: t('product.showColorVariants'),
      icon: 'FiGrid',
      onClick: () => {
        navigate(
          `/catalogue/${genre}/${category}/${id}/list?itemCode=${itemCode}&mainFabricCode=${mainFabricCode}&itemColorCode=${itemColorCode}`
        );
      },
    },
  ];

  const buttonComponents = buttonsData?.map((item: ButtonType) =>
    !item.isHidden ? (
      <Button
        motive="actionQuaternary"
        fontFamily="LFT Zeno"
        scale="base"
        key={item.id}
        onClick={item.onClick}
        iconLeft={item.icon}
      >
        {item.label}
      </Button>
    ) : undefined
  );

  return {
    buttonComponents,
  };
};

export { useActions };
